()
<template>
  <v-card class="signing-card" flat>
    <DocumentMenu
      ref="documentMenu"
      :document="document"
      :policy="filteredPolicies"
      :sign-key="signKey"
    >
      <template #activator="{ props: ActivatorProps }">
        <v-btn
          v-bind="ActivatorProps"
          aria-label="Document menu"
          class="bg-transparent"
          data-test-id="signing-card-options"
          icon
          style="position: absolute; right: 4px; top: 8px"
        >
          <VIcon class="text-grey-darken-1"> far fa-ellipsis-vertical </VIcon>
        </v-btn>
      </template>
    </DocumentMenu>
    <div class="d-flex flex-row">
      <div class="d-flex flex-column w-100">
        <!-- TITLE -->
        <div class="d-flex flex-row text-body-medium pb-1 rounded-0">
          {{
            isSignatureTypeSeal
              ? i18n.t('titles.sealed_as')
              : isSignedByUser
                ? i18n.t('titles.signed_as')
                : i18n.t('titles.signing_as')
          }}
        </div>
        <!-- /TITLE -->

        <!-- REQUESTOR DETAILS -->
        <SignerSignDetails
          :email="
            isSignatureTypeSeal ? document?.initiatorCompanyDisplayName : email
          "
        >
          <template #signature-level>
            <DocumentSignatureLevel
              v-if="signStore.document?.signContext.signatureType !== 'seal'"
              :jurisdiction="signStore.document?.jurisdiction"
              :signature-mode="signStore.document?.signatureMode"
            />
          </template>
          <template v-if="ownSignatureStatus === SignStatus.SIGNED" #checkmark>
            <v-icon
              class="status-icon mr-1"
              color="success"
              data-test-id="sign-success-icon"
              size="18"
            >
              fas fa-circle-check
            </v-icon>
          </template>
        </SignerSignDetails>
        <!-- /REQUESTOR DETAILS -->
      </div>
      <DocumentDialog />
    </div>
    <!-- VISUAL SIGNATURE -->
    <div v-if="ownSignatureStatus !== SignStatus.SIGNED && userIsVerified">
      <div class="d-flex justify-center">
        <GuestSignaturePreview
          v-show="isGuest && showSignaturePad"
          class="w-100"
          :is-signature-placed="showSignaturePad"
        />
        <AuthenticatedSignaturePreview v-show="!isGuest && showSignaturePad" />
      </div>
      <!-- /STORED VISUAL SIGNATURE -->
      <div
        v-if="
          isSignaturePlaced &&
          (signStore.document?.documentPolicy.canModifyAutographPosition ||
            canPlaceOnSignStep)
        "
        class="pt-2 mb-2"
      >
        <MoveSignaturesMenu
          v-if="
            signStore.document?.documentPolicy.canModifyAutographPosition ||
            canPlaceOnSignStep
          "
          v-model="ownSignature"
          :update-only-one="true"
        />
      </div>
    </div>
    <!-- /VISUAL SIGNATURE -->
    <!-- ON HOLD ALERT -->
    <v-alert
      v-if="ownSignatureStatus === SignStatus.ON_HOLD"
      class="deep-caption my-4"
      type="info"
      variant="outlined"
    >
      {{
        waitingOnApproval
          ? i18n.t('texts.on_hold_approval_hint')
          : i18n.t(
              hasMultipleFiles
                ? 'texts.on_hold_hint_envelope'
                : 'texts.on_hold_hint'
            )
      }}
    </v-alert>
    <!-- /ON HOLD ALERT -->

    <!-- ACTIONS SECTION -->
    <div
      v-if="ownSignatureStatus !== SignStatus.SIGNED"
      class="d-flex flex-row justify-center align-center"
    >
      <div
        class="d-flex flex-column mb-2 actions-section"
        :class="{ 'align-center': !isSignedByUser }"
      >
        <!-- ATTACHMENTS -->
        <div
          v-if="
            canModifySigneeAttachments &&
            ownSignatureStatus !== SignStatus.ON_HOLD
          "
          class="w-100 d-flex flex-column ga-4"
        >
          <v-divider class="mt-4" />
          <Attachments
            v-model="uploadedFiles"
            attachment-type="signee-attachment"
            :max-file-size-m-b="40"
            supported-types="application/pdf"
            @remove-file="handleRemove"
            @update:model-value="handleUpload"
          />
          <v-divider class="mb-4 mt-2" />
        </div>
        <!-- /ATTACHMENTS -->

        <!-- ACTIONS -->

        <div class="actions-container d-flex flex-column w-100">
          <!-- PLACE SIGNATURE -->

          <DeepButton
            v-if="showPlaceSignaturesButton"
            :action="onPlaceSignature"
            block
            class="mt-2 w-100"
            data-test-id="place-signature"
            :disabled="
              isSigneePlaceholderPlaced ||
              !globalStore.pdfPreviewRendered ||
              ownSignatureStatus === SignStatus.ON_HOLD
            "
            :text="signatureButtonText"
          />
          <!-- /PLACE SIGNATURE -->

          <!-- IDENTIFY -->
          <DeepButton
            v-if="hasSigneeIssues"
            :action="openIdentifyDialog"
            class="mt-2"
            data-test-id="itentify-btn"
            :text="i18n.t('buttons.identify')"
          />
          <!-- /IDENTIFY -->

          <!-- SIGN -->
          <DeepButton
            v-if="!isSignedByUser && !hideSignButton"
            :action="sign"
            class="mt-2"
            data-test-id="sign-now"
            :disabled="
              !signButtonEnabled ||
              ownSignatureStatus === SignStatus.ON_HOLD ||
              areAttachmentFilesInvalid ||
              hasSigneeIssues ||
              hasRequiredAutographUnsetted
            "
            :loading="signStore.signPending || signStore.mobileIdCheckPending"
            :text="signNowButtonText"
            :variant="
              !isSignaturePlaced &&
              autographRequirementLevel !==
                VisualSignatureRequirement.PROHIBITED &&
              !isQuorumGroupMemberOfAnyDocument
                ? 'outlined'
                : undefined
            "
          />
          <!-- /SIGN -->

          <!-- AUTHORITY SERVICE -->
          <v-row
            v-if="showAuthorityMenu"
            class="d-flex align-center authority-service deep-footnote"
            no-gutters
          >
            <v-spacer />
            <div class="mt-1">
              {{ i18n.t('labels.id_service') }}
            </div>
            <AuthorityServiceMenu
              v-if="availableAuthorityServices.length > 0"
              :authority-service="authorityService"
              :authority-services="availableAuthorityServices"
              :signature-mode="document.signatureMode"
            />
          </v-row>
          <!-- /AUTHORITY SERVICE -->

          <!-- /ACTIONS -->
        </div>
      </div>
    </div>
    <!-- /ACTIONS SECTION -->

    <!-- SIGNING DETAILS OF SIGNED DOCUMENTS -->
    <div v-else class="d-flex align-center justify-center">
      <span class="signed">
        {{
          isSignatureTypeSeal
            ? i18n.t('document_status.sealed')
            : i18n.t('document_status.signed')
        }}:
      </span>
      <div class="d-flex align-center">
        <span
          v-tooltip="{
            text: timestampToDate(ownSignature.completionTime),
            location: 'end',
            offset: 4,
            openDelay: 300
          }"
          :aria-label="timestampToDate(ownSignature.completionTime)"
          class="text-grey text-body-medium"
        >
          {{
            formatDateToLocale(
              ownSignature.completionTime,
              i18n.locale.value,
              true
            )
          }}
        </span>
      </div>
      <v-spacer />
    </div>
    <!-- IDENTIFY DIALOG -->
    <IdentifyDialog />
  </v-card>
</template>

<script setup lang="ts">
import SignerSignDetails from '@/components/signatureArea/SignerSignDetails.vue';
import MoveSignaturesMenu from '@/components/menus/MoveSignaturesMenu.vue';
import IdentifyDialog from '@/components/dialogs/IdentifyDialog.vue';
import GuestSignaturePreview from '@/components/signature-preview/GuestSignaturePreview.vue';
import DocumentSignatureLevel from '@/components/signatureArea/DocumentSignatureLevel.vue';
import DocumentMenu from '@/components/menus/DocumentMenu.vue';
import DocumentDialog from '@/components/dialogs/document-dialog/DocumentDialog.vue';
import DeepButton from '@/components/buttons/DeepButton.vue';
import AuthorityServiceMenu from '@/components/menus/AuthorityServiceMenu.vue';
import AuthenticatedSignaturePreview from '@/components/signature-preview/AuthenticatedSignaturePreview.vue';
import Attachments from '@/components/inputs/Attachments.vue';
import AuthService from '@/services/authService.ts';
import { useGlobalStore } from '@/stores/global.ts';
import { SignatureMode } from '@/types/enums/SignatureMode.ts';
import { AuthorityService } from '@/types/enums/AuthorityService.ts';
import { SignStatus } from '@/types/enums/SignStatus.ts';
import { BetaFeature } from '@/types/enums/BetaFeature.ts';
import { AttachmentStatus } from '@/types/enums/AttachmentStatus.ts';
import { useSignStore } from '@/stores/sign.ts';
import { usePreferencesStore } from '@/stores/deepadmin/preferences.ts';
import { computed, onUnmounted, type PropType, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useEmitter } from '@/composables/useEmitter.ts';
import { useI18n } from 'vue-i18n';
import { formatDateToLocale, orderBy } from '@/composables/useUtils';
import { useSignContext } from '@/composables/useSignContext';
import { useDocumentContext } from '@/composables/useDocumentContext';
import { VisualSignatureRequirement } from '@/types/enums/VisualSignatureRequirement';
import { v4 as uuid } from 'uuid';
import { SigneeType } from '@/types/enums/SigneeType';
import type { Document } from '@/types/Document';
import type { Signee } from '@/types/Signee';
import type { AttachedFile } from '@/types/FileType';
import { timestampToDate } from '@deepcloud/deep-ui-utils/date';
import { Jurisdiction } from '@/types/enums/Jurisdiction';
import { SignatureType } from '@/types/enums/SignatureType';
import { constants } from '@/constants/constants';
import {
  injectStrict,
  PlaceHolderComposableKey
} from '@/composables/placement/placementSymbols.ts';
import type { Placeholder } from '@/composables/placement/placementTypes.ts';
import { persistAutoPlacedSignStepPlaceholders } from '@/composables/placement/usePlacementUtility.ts';
import {
  canSigneeModifyAutographPositionOnFile,
  findSigneeGroupForSignee,
  isPlaceholderForSignee,
  isQuorumSlotForSignee,
  isSigneeGroupQuorumSet
} from '@/utils/signeeSignings';
import { useDisplay } from 'vuetify';
import { useSettingsDevStore } from '@/stores/settingsDev.ts';

const props = defineProps({
  authorityServiceKey: {
    type: String,
    default: null
  },
  document: {
    type: Object as PropType<Document>,
    default: undefined
  },
  signatures: {
    type: Array as PropType<Signee[]>,
    default: undefined
  },
  email: {
    type: String,
    default: ''
  },
  canAddAutograph: {
    type: Boolean,
    default: false
  },
  canSign: {
    type: Boolean,
    default: false
  },
  isSignedByUser: {
    type: Boolean,
    default: false
  },
  signButtonEnabled: {
    type: Boolean,
    default: false
  },
  sign: {
    type: Function,
    required: true
  }
});

const emit = defineEmits(['update-authority-service', 'update-uploaded-files']);

const display = useDisplay();
const globalStore = useGlobalStore();
const signStore = useSignStore();
const preferencesStore = usePreferencesStore();
const settingsDevStore = useSettingsDevStore();

const route = useRoute();
const emitter = useEmitter();
const i18n = useI18n();
const { policy, isSignatureTypeSeal, isGuest, signKey } = useSignContext();
const { currentDocument, shouldPersistSignPlacement } = useDocumentContext();
const {
  currentPlaceholders,
  placePlaceholdersAutomatically,
  placeholderTemplateRefs,
  requestPlaceholderRemount
} = injectStrict(PlaceHolderComposableKey);
const changeAutograph = ref(false);
const uploadedFiles = ref<AttachedFile[]>([]);
const authorityService = ref(null);
const ownSignatureModel = ref();

// A quorum group owns exactly `requiredSignatures` slots (a group-level data
// structure, not tied to any member), so "which placeholder is mine" can't be
// answered by `isPlaceholderForSignee` here: it matches a member to a slot by
// their FIXED ordinal in the group, while on the sign step slots are anonymous
// and claimed by whoever signs first (see `getQuorumSlotForSignee`). Ordinal
// matching leaves a member empty-handed whenever their ordinal doesn't happen
// to name a still-open, placed slot — e.g. an overflow member (ordinal >=
// `requiredSignatures`), or any member whose ordinal-slot was already taken by
// someone else — which in turn makes `isSignaturePlaced` false and hides the
// signature pad even though the signee does have a slot to fill.
function matchesSigneePlaceholder(
  placeholder: Placeholder,
  signeeId: string
): boolean {
  const signeeGroups = (currentDocument.value as Document | undefined)
    ?.signeeGroups;
  if (placeholder.isQuorumSlot) {
    return isQuorumSlotForSignee(placeholder, signeeId, signeeGroups);
  }
  return isPlaceholderForSignee(placeholder, signeeId, signeeGroups);
}

const isSigneePlaceholderPlaced = computed(() => {
  const signeeId = signStore.document?.signContext.signeeId;
  if (!signeeId) return false;
  const match = currentPlaceholders.value.find((placeholder) =>
    matchesSigneePlaceholder(placeholder, signeeId)
  );
  return match?.isPlaced || false;
});

const filteredPolicies = computed(() => {
  const isSeal =
    currentDocument.value?.signContext?.signatureType === SignatureType.SEAL;
  if (isSeal) {
    return Object.keys(policy?.value)
      .filter((key) => !constants.SEAL_POLICIES_TO_OMIT.includes(key))
      .reduce((acc: Record<string, unknown>, key) => {
        acc[key] = policy?.value[key];
        return acc;
      }, {});
  } else {
    return policy.value;
  }
});

const isSignaturePlaced = computed(() => {
  const signeeId = signStore.document?.signContext.signeeId;
  if (!signeeId) return false;
  return currentPlaceholders.value.some(
    (placeholder: Placeholder) =>
      matchesSigneePlaceholder(placeholder, signeeId) &&
      placeholder.isPlaced &&
      placeholder.isVisible
  );
});

const hasRequiredAutographUnsetted = computed(() => {
  return (
    currentDocument.value?.autographRequired &&
    !(
      signStore.document?.signContext.defaultAutographUrl ||
      signStore.autographData
    ) &&
    isSignaturePlaced.value
  );
});

const autographRequirementLevel = computed(() => {
  return currentDocument.value?.visualSignatureRequirement;
});

const hasMultipleFiles = computed(() => {
  return (props.document?.files?.length ?? 0) > 1;
});

const hideSignButton = computed(() => {
  return (
    autographRequirementLevel.value === VisualSignatureRequirement.REQUIRED &&
    !isSignaturePlaced.value
  );
});

const signNowButtonText = computed(() => {
  return hasMultipleFiles.value
    ? i18n.t('buttons.sign_all_documents', {
        number: props.document?.files?.length
      })
    : i18n.t('buttons.sign_now');
});

// Whether the current signee belongs to a quorum-set signee group, on any
// document (not restricted to envelopes, unlike `isQuorumGroupMember`
// below). Such a member never gets to self-place a placeholder on the sign
// step: the group's shared slot is either pre-placed by the initiator during
// document creation, or it simply isn't placeable here at all.
const isQuorumGroupMemberOfAnyDocument = computed(() => {
  const signeeId = signStore.document?.signContext.signeeId;
  if (!signeeId) return false;
  return isSigneeGroupQuorumSet(
    findSigneeGroupForSignee(
      signeeId,
      (currentDocument.value as Document | undefined)?.signeeGroups
    )
  );
});

// In an envelope, members of a quorum-enabled signee group share group-owned
// signature slots instead of owning per-signee autograph positions, so this
// signee never gets their own signature pad.
const isQuorumGroupMember = computed(() => {
  if (!signStore.isEnvelope) return false;
  return isQuorumGroupMemberOfAnyDocument.value;
});

// Signature pad normally appears after the placeholder is placed on the PDF.
// In an envelope, document-level canModifyAutographPosition can't be relied on
// to gate this: a per-file signing policy can still allow placement even when
// it reads false at the document/signee level (see shouldPersistSignPlacement
// in useDocumentContext.ts). So, like showPlaceSignaturesButton below, the
// envelope case is gated on shouldPersistSignPlacement instead — but it must
// still also require isSignaturePlaced, otherwise the pad renders before the
// signee has placed anything, showing both it and the "Place signature"
// button at once.
const showSignaturePad = computed(() => {
  if (
    autographRequirementLevel.value === VisualSignatureRequirement.PROHIBITED
  ) {
    return false;
  }
  if (isQuorumGroupMember.value && isSignaturePlaced.value) {
    return true;
  } else if (isQuorumGroupMember.value && !isSignaturePlaced.value) {
    return false;
  }
  if (signStore.isEnvelope && canPlaceOnSignStep.value) {
    return canPlaceOnSignStep.value && isSignaturePlaced.value;
  }
  return isSignaturePlaced.value;
});

const signaturesToPlace = computed(() => {
  const filteredSignees = props.signatures.filter(
    (signee) => signee.signeeType === SigneeType.SIGN
  );
  switch (route.name) {
    case 'document-sign':
    case 'sign-id':
      if (
        signStore.document?.documentPolicy.canModifyAutographPosition ||
        canPlaceOnSignStep.value
      ) {
        return props.signatures.filter(
          (signee) =>
            signee.signeeId === signStore.document?.signContext.signeeId
        );
      }
      return [];
    case 'document-create':
      return filteredSignees;
    case 'document-seal':
      return filteredSignees;
    default:
      return [];
  }
});

if (props.signatures) {
  ownSignatureModel.value = props.signatures.find(
    (signee) =>
      signee.signeeId === signStore.document?.signContext.signeeId &&
      signee.signeeType === SigneeType.SIGN
  );
}

const ownSignature = computed({
  get: () =>
    props.signatures.find(
      (signee) =>
        signee.signeeId === signStore.document?.signContext.signeeId &&
        signee.signeeType === SigneeType.SIGN
    ),
  set: (newValue) => {
    ownSignatureModel.value = newValue;
  }
});

// Whether the sign-step envelope override (`shouldPersistSignPlacement`:
// envelope + sign route + isSignee + no quorum for the signee's group) also
// actually grants THIS signee permission to place/move/resize a signature on
// the currently selected file — via the signee-level or per-file signing
// policy (see `canSigneeModifyAutographPositionOnFile`). The document-level
// policy is already checked separately wherever this is combined below.
const canPlaceOnSignStep = computed(() => {
  if (!shouldPersistSignPlacement.value || !ownSignature.value) return false;
  return canSigneeModifyAutographPositionOnFile(
    ownSignature.value,
    globalStore.selectedDocumentFileId,
    signStore.document
  );
});

const showPlaceSignaturesButton = computed(() => {
  if (signStore.isEnvelope && shouldPersistSignPlacement.value) {
    return (
      canPlaceOnSignStep.value &&
      !isSignaturePlaced.value &&
      !hasSigneeIssues.value
    );
  }
  if (isQuorumGroupMemberOfAnyDocument.value) {
    return false;
  }
  if (autographRequirementLevel.value === VisualSignatureRequirement.PROHIBITED)
    return false;

  if (!props.canAddAutograph) return false;

  if (route.name === 'document-details') {
    return false;
  }
  if (route.name === 'sign-id') {
    return (
      ownSignature.value?.autographPosition === null && !hasSigneeIssues.value
    );
  }
  return !isSignaturePlaced.value && !hasSigneeIssues.value;
});

const ownSignatureStatus = computed(() => ownSignature.value?.signStatus || '');
const waitingOnApproval = computed(() => {
  return !!props.signatures.find(
    (signee) =>
      (signee.signeeType === SigneeType.APPROVE &&
        signee.signStatus === SignStatus.ON_HOLD) ||
      signee.signStatus === SignStatus.PENDING
  );
});

const canModifySigneeAttachments = computed(() => {
  return policy.value.canModifySigneeAttachments;
});

const areAttachmentFilesInvalid = computed(() => {
  return uploadedFiles.value.filter(
    (file) => file.status !== AttachmentStatus.ATTACHED
  ).length;
});

const isMobileIdVerified = computed(
  () =>
    signStore.document &&
    signStore.document.signContext.availableAuthorityServices.ras &&
    signStore.document.signContext.availableAuthorityServices.ras
      .hasRequiredLevel
);

const isDeepIdVerified = computed(() => {
  return (
    signStore.document &&
    signStore.document.signContext.availableAuthorityServices.did &&
    signStore.document.signContext.availableAuthorityServices.did
      .hasRequiredLevel
  );
});

const availableAuthorityServices = computed(() => {
  let services = [];
  if (signStore.document) {
    services = Object.entries(
      signStore.document.signContext.availableAuthorityServices
    );
  }

  const availableServices = [];
  services.forEach((service) => {
    if (service[0] !== AuthorityService.YAPEAL_ID) {
      availableServices.push({
        key: service[0],
        ...service[1]
      });
    }
  });

  if (!canUseDeepID.value) {
    return availableServices.filter(
      (service) => service.key === AuthorityService.MOBILE_ID
    );
  }
  const orderedServices = orderBy(availableServices, ['key']);

  return settingsDevStore.canUseDevFeature('DEEPSIGN_SIGN8') ||
    props.document?.requiredAuthorityService === AuthorityService.SIGN8
    ? orderedServices
    : orderedServices.filter((mode) => mode.key !== AuthorityService.SIGN8);
});

const hasSigneeIssues = computed(
  () => authorityService.value?.signeeIssues.length > 0 || false
);

const signatureButtonText = computed(() => {
  if (route.name === 'document-seal') {
    return i18n.t('buttons.place_seal');
  } else {
    return i18n.t('buttons.place_signatures', signaturesToPlace.value.length);
  }
});

const showAuthorityMenu = computed(
  () => props.document?.signatureMode !== SignatureMode.TIMESTAMP
);

const userIsVerified = computed(() => authorityService.value?.hasRequiredLevel);

const canUseDeepID = computed(() => {
  return (
    import.meta.env.VITE_AUTHORITY_SERVICE_ENABLED === 'true' ||
    AuthService.hasBetaFeature(BetaFeature.DEEP_ID_SIGN)
  );
});

emitter.$on('update-change-autograph', updateChangeAutograph);
emitter.$on('set-authority-service', setAuthorityService);
emitter.$on('enable-sms-code', enableSmsCode);
emitter.$on('enable-deep-id', enableDeepId);

getAuthorityService();

if (authorityService.value === null || authorityService.value === undefined) {
  const signatureMode = props.document?.signatureMode;
  const jurisdiction = props.document?.jurisdiction;

  const lastAuthorityServiceKey =
    preferencesStore.preferences?.defaultAuthorityService;
  const lastAuthorityService = availableAuthorityServices.value.find(
    (service) => service.key === lastAuthorityServiceKey
  );
  // If enforced authority service
  if (props.document && props.document.requiredAuthorityService) {
    authorityService.value = availableAuthorityServices.value.find(
      (a) => a.key === props.document?.requiredAuthorityService
    );
  }
  // Else, if there is a prefered authority service and you are either
  // MobileId verified or DeepID verified
  else if (
    lastAuthorityService &&
    (isMobileIdVerified.value || isDeepIdVerified.value)
  ) {
    authorityService.value = lastAuthorityService;
  }
  // Else, we use the logic defined on DEEPBOX-5273
  else {
    if (
      signatureMode === SignatureMode.TIMESTAMP ||
      (signatureMode === SignatureMode.ADVANCED &&
        jurisdiction === Jurisdiction.ZERTES &&
        (isMobileIdVerified.value ||
          (!isMobileIdVerified.value && !isDeepIdVerified.value)))
    ) {
      authorityService.value = availableAuthorityServices.value.find(
        (a) => a.key === AuthorityService.MOBILE_ID
      );
    } else {
      authorityService.value = availableAuthorityServices.value.find(
        (a) => a.key === AuthorityService.DEEP_ID
      );
    }
  }
}

signStore.document?.signContext.signeeAttachments.forEach((attachment) => {
  uploadedFiles.value.push({
    file: null,
    status: AttachmentStatus.ATTACHED,
    attachment
  });
});

onUnmounted(() => {
  emitter.$off('update-change-autograph', updateChangeAutograph);
  emitter.$off('set-authority-service', setAuthorityService);
  emitter.$off('enable-sms-code', enableSmsCode);
  emitter.$off('enable-deep-id', enableDeepId);
});

async function onPlaceSignature() {
  const placedPlaceholders = await placePlaceholdersAutomatically(
    signaturesToPlace.value
  );

  if (shouldPersistSignPlacement.value) {
    await persistAutoPlacedSignStepPlaceholders(
      signStore,
      signaturesToPlace.value?.[0],
      placedPlaceholders,
      currentPlaceholders.value as Placeholder[],
      placeholderTemplateRefs.value,
      requestPlaceholderRemount
    );
  }

  if (display.mdAndDown.value) {
    globalStore.drawer = false;
  }
}

function updateChangeAutograph(value) {
  changeAutograph.value = value;
}

async function handleUpload(filesToUpload) {
  uploadedFiles.value = filesToUpload;
  const uploadPromises = uploadedFiles.value.map(async (file) => {
    if (
      file.status === AttachmentStatus.PENDING &&
      currentDocument.value?.documentId
    ) {
      try {
        file.status = AttachmentStatus.PROGRESS;

        const response = await signStore.postAttachment(
          currentDocument.value?.documentId,
          file.file
        );

        const { signeeAttachmentId, name, size, mimeType } = response.data;

        const newFileData = {
          attachmentId: signeeAttachmentId,
          name,
          size,
          mimeType,
          uploadId: file.uploadId,
          status: AttachmentStatus.ATTACHED
        };

        uploadedFiles.value = uploadedFiles.value?.map((fileItem) => {
          return fileItem.uploadId === file.uploadId ? newFileData : fileItem;
        });

        emit('update-uploaded-files', uploadedFiles.value);
      } catch (error) {
        file.status = AttachmentStatus.ERROR;

        file.errorId = error.response?.data?.messageId || 'Unknown error';
      }
    }
  });

  await Promise.all(uploadPromises);
}

function selectKey(file) {
  if (file?.attachmentId)
    return { key: 'attachmentId', value: file.attachmentId };
  if (file?.uploadId) return { key: 'uploadId', value: file.uploadId };
  if (file?.attachment?.signeeAttachmentId)
    return {
      key: 'signeeAttachmentId',
      value: file.attachment.signeeAttachmentId
    };
  return { key: null, value: null };
}

function matchesByKey(key, value, f) {
  if (!key || !value) return false;
  if (key === 'attachmentId') return f?.attachmentId === value;
  if (key === 'uploadId') return f?.uploadId === value;
  if (key === 'signeeAttachmentId')
    return f?.attachment?.signeeAttachmentId === value;
  return false;
}

async function handleRemove(fileToRemove) {
  const { key, value } = selectKey(fileToRemove);
  if (!key || !value) return;

  uploadedFiles.value = uploadedFiles.value.map((f) =>
    matchesByKey(key, value, f)
      ? { ...f, status: AttachmentStatus.DISABLED }
      : f
  );

  try {
    const docId = currentDocument.value?.documentId;
    const isServerKey = key === 'attachmentId' || key === 'signeeAttachmentId';
    if (docId && isServerKey) {
      const serverId = key === 'attachmentId' ? value : value;
      await signStore.deleteAttachment(docId, serverId);
    }

    uploadedFiles.value = uploadedFiles.value.filter(
      (f) => !matchesByKey(key, value, f)
    );
    emit('update-uploaded-files', uploadedFiles.value);
  } catch (error) {
    console.error('Error removing attachment:', error);
    uploadedFiles.value = uploadedFiles.value.map((f) => {
      if (!matchesByKey(key, value, f)) return f;
      const hadServerId = !!(
        f?.attachmentId || f?.attachment?.signeeAttachmentId
      );
      return {
        ...f,
        status: hadServerId
          ? AttachmentStatus.ATTACHED
          : AttachmentStatus.PENDING
      };
    });
  }
}

function setAuthorityService(event) {
  authorityService.value = availableAuthorityServices.value.find(
    (service) => service.key === event
  );
}

function openIdentifyDialog() {
  emitter.$emit('open-identify-dialog', {
    authorityService: authorityService.value,
    signatureMode: props.document.signatureMode
  });
}

function getAuthorityService() {
  authorityService.value = availableAuthorityServices.value.find(
    (service) => service.key === props.authorityServiceKey
  );
}

function enableSmsCode() {
  authorityService.value = availableAuthorityServices.value.find(
    (service) => service.key === 'ras'
  );
}

function enableDeepId() {
  authorityService.value = availableAuthorityServices.value.find(
    (service) => service.key === 'did'
  );
}

async function updateDefaultAutograph() {
  // Validate if a string is a URL
  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Check if defaultAutographUrl exists and is a valid URL
  const autographUrl = signStore.document?.signContext.defaultAutographUrl;
  if (
    !autographUrl ||
    !isValidUrl(autographUrl) ||
    !autographUrl.startsWith('https://')
  ) {
    return;
  }

  try {
    const response = await fetch(autographUrl);
    if (!response.ok) {
      throw new Error(`Error fetching image: ${response.statusText}`);
    }
    const imageBlob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (signStore.document && reader.result) {
          signStore.document.signContext.defaultAutographUrl =
            reader.result as string;
        }

        resolve();
      };
      reader.onerror = () => {
        signStore.document!.signContext.defaultAutographUrl = null;
        reject(new Error('Error converting image to Base64'));
      };
      reader.readAsDataURL(imageBlob);
    });
  } catch (error) {
    console.error('Error downloading image:', error);
    signStore.document!.signContext.defaultAutographUrl = null;
  }
}

watch(
  () => signStore.document,
  (newValue) => {
    if (newValue?.signContext?.defaultAutographUrl) {
      updateDefaultAutograph();
    }
  },
  {
    immediate: true,
    deep: true
  }
);

watch(
  () => authorityService.value,
  (value) => {
    emit('update-authority-service', value?.key || null);
  },
  {
    immediate: true,
    deep: true
  }
);

watch(
  () => signStore.document?.signContext.signeeAttachments,
  (newValue) => {
    if (newValue) {
      uploadedFiles.value = newValue.map((file) => {
        return {
          attachmentId: file.signeeAttachmentId,
          mimeType: file.mimeType,
          name: file.name,
          size: file.size,
          uploadId: uuid(),
          status: AttachmentStatus.ATTACHED
        };
      });
      emit('update-uploaded-files', uploadedFiles.value);
    }
  },
  {
    deep: true,
    immediate: true
  }
);
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/styles/settings.scss' as *;

.signing-card {
  box-sizing: border-box;
  border-radius: 7px;
  background-color: rgb(var(--v-theme-grey-lighten-5));
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid rgb(var(--v-theme-grey-lighten-2));
  margin-bottom: 32px;
  margin-top: 16px;
  padding: 1rem;
}

.signee-email {
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0.0025em;
  color: black !important;
  padding-bottom: 8px;

  &.mobile {
    font-size: 16px;
  }
}

.signature-level {
  color: rgb(var(--v-theme-grey));
  font-weight: 400 !important;
  margin-right: 6px;
  font-size: 14px !important;
}

.actions-section {
  width: 100%;
}

.actions-container {
  max-width: 380px;

  @media #{map.get($display-breakpoints, 'lg-and-up')} {
    max-width: none;
  }
}

.signed {
  margin-right: 4px;
  color: rgb(var(--v-theme-success));
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
}

.signed-icon {
  margin-top: -28px;
  color: rgb(var(--v-theme-success));
}

.date {
  color: rgb(var(--v-theme-grey));
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
}

.default-autograph {
  margin-top: 16px;
  margin-bottom: 10px;
  width: 100%;
  aspect-ratio: 196/48;
  border: 1px solid rgb(var(--v-theme-grey));
  border-radius: 4px;
}

.change-default-autograph {
  width: 100%;
  padding-left: 0;
  padding-right: 0;
}

.change-button {
  &:hover {
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
  }

  &:hover :deep(.v-icon.v-icon) {
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
  }
}

.menu-icon {
  color: rgb(var(--v-theme-grey));
}

.tooltip {
  box-shadow: 0 3px 10px rgb(0 0 0 / 0.2) !important;
}

.deep-caption-grey {
  color: rgb(var(--v-theme-grey-lighten-1));
}

.too-large {
  color: rgb(var(--v-theme-red-accent-2));
}

.authority-service {
  color: rgb(var(--v-theme-grey-lighten-1));
  margin-top: 8px;
  width: 100%;
}
</style>
